import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { useFormFields, isEmail } from "@/hooks/useFormFields";
import { CvUploadField } from "@/components/careers/CvUploadField";

export const TIPOLOGIA_OPTIONS = ["Tirocinio Anticipato", "Pratica Post-Laurea"];
const EMPTY = { nome: "", email: "", telefono: "", universita: "", tipologia: "", media: "", messaggio: "", gdpr: false };
const toNumber = (v) => parseFloat(String(v).replace(",", "."));

const VALIDATORS = {
  nome: (v) => v.trim().length < 2 && "Inserisci nome e cognome",
  email: (v) => !isEmail(v) && "Inserisci un indirizzo email valido",
  telefono: (v) => v.trim().length < 6 && "Inserisci un numero di telefono valido",
  universita: (v) => v.trim().length < 2 && "Indica l'Università di provenienza",
  tipologia: (v) => !v && "Seleziona la tipologia di candidatura",
  media: (v) => (Number.isNaN(toNumber(v)) || toNumber(v) < 18 || toNumber(v) > 30) && "Inserisci una media compresa tra 18 e 30",
  messaggio: (v) => v.trim().length < 10 && "Scrivi una breve presentazione (min. 10 caratteri)",
  gdpr: (v) => !v && "Il consenso al trattamento dei dati è obbligatorio",
};

const Field = ({ id, label, error, children }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600" data-testid={`app-error-${id.replace("app-", "")}`}>{error}</p>}
  </div>
);

export const ApplicationForm = () => {
  const { form, errors, set, validate, reset, setError } = useFormFields(EMPTY, VALIDATORS);
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    const ok = validate();
    if (!cv) setError("cv", "Allega il tuo CV in formato PDF o Word");
    if (!ok || !cv) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === "media" ? String(toNumber(v)) : v));
      fd.append("cv", cv);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Candidatura inviata con successo", { description: "Lo Studio esaminerà il tuo profilo e ti ricontatterà ai recapiti indicati." });
      reset();
      setCv(null);
    } catch (err) {
      toast.error("Invio non riuscito", { description: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-navy/10 bg-white p-7 sm:p-10 shadow-sm" data-testid="application-form" noValidate>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field id="app-nome" label="Nome e Cognome *" error={errors.nome}>
          <Input id="app-nome" data-testid="app-input-nome" className="mt-2" value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Mario Rossi" />
        </Field>
        <Field id="app-email" label="Email *" error={errors.email}>
          <Input id="app-email" type="email" data-testid="app-input-email" className="mt-2" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mario.rossi@email.it" />
        </Field>
        <Field id="app-telefono" label="Telefono *" error={errors.telefono}>
          <Input id="app-telefono" type="tel" data-testid="app-input-telefono" className="mt-2" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+39 333 000 0000" />
        </Field>
        <Field id="app-universita" label="Università *" error={errors.universita}>
          <Input id="app-universita" data-testid="app-input-universita" className="mt-2" value={form.universita} onChange={(e) => set("universita", e.target.value)} placeholder="Università degli Studi di Napoli Federico II" />
        </Field>
        <Field id="app-tipologia" label="Tipologia Candidatura *" error={errors.tipologia}>
          <Select value={form.tipologia} onValueChange={(v) => set("tipologia", v)}>
            <SelectTrigger className="mt-2" data-testid="app-select-tipologia"><SelectValue placeholder="Seleziona la tipologia" /></SelectTrigger>
            <SelectContent>
              {TIPOLOGIA_OPTIONS.map((t) => <SelectItem key={t} value={t} data-testid={`app-tipologia-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field id="app-media" label="Media Ponderata Esami (su 30) *" error={errors.media}>
          <Input id="app-media" type="number" step="0.01" min="18" max="30" inputMode="decimal" data-testid="app-input-media" className="mt-2" value={form.media} onChange={(e) => set("media", e.target.value)} placeholder="27.50" />
        </Field>

        <CvUploadField file={cv} onChange={(f) => { setCv(f); setError("cv", undefined); }} onError={(msg) => setError("cv", msg)} error={errors.cv} />

        <div className="sm:col-span-2">
          <Field id="app-messaggio" label="Messaggio di Presentazione *" error={errors.messaggio}>
            <Textarea id="app-messaggio" data-testid="app-input-messaggio" className="mt-2 min-h-[140px]" value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)} placeholder="Raccontaci il tuo percorso, le tue motivazioni e le aree del diritto che ti appassionano." />
          </Field>
        </div>

        <div className="sm:col-span-2 flex items-start gap-3">
          <Checkbox id="app-gdpr" data-testid="app-checkbox-gdpr" checked={form.gdpr} onCheckedChange={(v) => set("gdpr", !!v)} className="mt-0.5" />
          <div>
            <Label htmlFor="app-gdpr" className="text-sm font-normal leading-relaxed text-slate-600">
              Acconsento al trattamento dei miei dati personali e del curriculum allegato ai sensi del Regolamento UE 2016/679 (GDPR), ai soli fini della selezione del personale. *
            </Label>
            {errors.gdpr && <p className="mt-1 text-xs text-red-600" data-testid="app-error-gdpr">{errors.gdpr}</p>}
          </div>
        </div>
      </div>

      <button type="submit" disabled={loading} data-testid="application-submit-button" className="group mt-8 inline-flex items-center gap-3 bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
        {loading ? "Invio in corso..." : "Invia Candidatura"}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};
